import { useEffect, useState } from "react";
import { api } from "../api/client";
import { theme, type, emptyFrame } from "../theme";
import { SheetCard, RulerProgress } from "./Drawing";

// The continuous-monitoring trend on a connector page: each measure the connector's sync records
// once a day (backend lib/connectorTrends.ts), over the last 90 days. The same series is written
// into the connector evidence document, so what the page shows is what an assessor reads.

export type TrendConnector = "m365_security" | "intune" | "aws" | "gcp" | "sentinel";

interface TrendPoint { day: string; value: number }
interface MetricTrend {
  key: string;
  label: string;
  unit: "pct" | "count";
  better: "higher" | "lower";
  points: TrendPoint[];
  first: TrendPoint | null;
  latest: TrendPoint | null;
  min: number | null;
  max: number | null;
  change: number | null;
  direction: "improving" | "declining" | "steady" | "insufficient";
  daysRecorded: number;
  windowDays: number;
  readingStale: boolean;
}

const DIRECTION: Record<MetricTrend["direction"], { word: string; color: string }> = {
  improving:    { word: "Improving",       color: "var(--c-successText)" },
  declining:    { word: "Declining",       color: "var(--c-dangerText)" },
  steady:       { word: "Steady",          color: "var(--c-muted)" },
  insufficient: { word: "One reading so far", color: "var(--c-muted)" },
};

const fmt = (unit: MetricTrend["unit"], v: number | null | undefined) =>
  v == null ? "—" : unit === "pct" ? `${Math.round(v * 10) / 10}%` : String(Math.round(v));
const DAY_MS = 86_400_000;

/** The vertical scale of the line: percentages from a floor just under the lowest reading (to the
 *  nearest 10) up to 100, so a movement of a few points is visible; counts from zero. */
function scaleOf(t: MetricTrend): { floor: number; top: number } {
  if (t.unit === "pct") return { floor: Math.max(0, Math.floor(((t.min ?? 0) - 5) / 10) * 10), top: 100 };
  return { floor: 0, top: Math.max(1, t.max ?? 1) };
}

/**
 * A small line over the 90-day window: one slot per day, a break wherever a day has no reading
 * (gaps are shown, never joined across), a dot for an isolated reading.
 */
function Sparkline({ t }: { t: MetricTrend }) {
  const W = 270, H = 44, PAD = 3;
  const today = Date.parse(`${new Date().toISOString().slice(0, 10)}T00:00:00Z`);
  const slot = (day: string) => t.windowDays - 1 - Math.round((today - Date.parse(`${day}T00:00:00Z`)) / DAY_MS);
  const { floor, top } = scaleOf(t);
  const x = (i: number) => PAD + (i / Math.max(1, t.windowDays - 1)) * (W - 2 * PAD);
  const y = (v: number) => H - PAD - ((v - floor) / Math.max(1, top - floor)) * (H - 2 * PAD);

  const runs: Array<Array<{ i: number; v: number }>> = [];
  let prev = -2;
  for (const p of t.points) {
    const i = slot(p.day);
    if (i < 0 || i >= t.windowDays) continue;
    if (i !== prev + 1) runs.push([]);
    runs[runs.length - 1].push({ i, v: p.value });
    prev = i;
  }
  const summary = `${t.label}: ${t.daysRecorded} readings in ${t.windowDays} days, from ${fmt(t.unit, t.first?.value)} to ${fmt(t.unit, t.latest?.value)}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={summary} preserveAspectRatio="none" style={{ display: "block" }}>
      <title>{summary}</title>
      <line x1={PAD} x2={W - PAD} y1={H - PAD} y2={H - PAD} stroke="var(--c-line)" strokeWidth={1} />
      <line x1={PAD} x2={W - PAD} y1={y(top)} y2={y(top)} stroke="var(--c-line)" strokeWidth={0.5} strokeDasharray="2 3" />
      {runs.map((run, k) => run.length === 1
        ? <circle key={k} cx={x(run[0].i)} cy={y(run[0].v)} r={2} fill="var(--c-accent)" />
        : <polyline key={k} points={run.map((p) => `${x(p.i)},${y(p.v)}`).join(" ")} fill="none" stroke="var(--c-accent)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />)}
      {t.latest && slot(t.latest.day) >= 0 && (
        <line x1={x(slot(t.latest.day))} x2={x(slot(t.latest.day))} y1={PAD} y2={H - PAD} stroke="var(--c-mark)" strokeWidth={1} />
      )}
    </svg>
  );
}

function MetricRow({ t }: { t: MetricTrend }) {
  const dir = DIRECTION[t.direction];
  const change = t.change == null ? null : `${t.change > 0 ? "+" : ""}${t.unit === "pct" ? `${t.change} pts` : t.change}`;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "8px 20px", alignItems: "center", padding: "10px 0", borderTop: "1px solid var(--c-line)" }}>
      <div style={{ minWidth: 0 }}>
        <div style={type.statLabel}>{t.label}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
          <span style={type.stat}>{fmt(t.unit, t.latest?.value)}</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: dir.color }}>{dir.word}{change ? ` · ${change}` : ""}</span>
        </div>
        {t.unit === "pct" && t.latest && <RulerProgress value={t.latest.value} label={`${t.label} ${fmt("pct", t.latest.value)}`} height={10} style={{ marginTop: 6 }} />}
      </div>
      <div style={{ minWidth: 0 }}>
        <Sparkline t={t} />
        <div style={{ ...type.meta, marginTop: 4 }}>
          {t.daysRecorded} of {t.windowDays} days recorded
          {t.first ? ` · first ${fmt(t.unit, t.first.value)} on ${t.first.day}` : ""}
          {t.min != null && t.max != null ? ` · range ${fmt(t.unit, t.min)} – ${fmt(t.unit, t.max)}` : ""}
          {` · scale ${fmt(t.unit, scaleOf(t).floor)} – ${fmt(t.unit, scaleOf(t).top)}`}
        </div>
        {t.readingStale && t.latest && (
          <div style={{ fontSize: 12, color: "var(--c-warningText)", marginTop: 2 }}>
            No reading since {t.latest.day}: the sync has stopped reporting, so operation after that date is not shown.
          </div>
        )}
      </div>
    </div>
  );
}

export function ConnectorTrendPanel({ connector }: { connector: TrendConnector }) {
  const [trends, setTrends] = useState<MetricTrend[] | null>(null);
  const [windowDays, setWindowDays] = useState(90);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api.get<{ windowDays: number; trends: MetricTrend[] }>("/connector-trends", { connector })
      .then((d) => { if (live) { setTrends(d.trends); setWindowDays(d.windowDays); } })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [connector]);

  if (failed) return null;   // the trend is supplementary; the page works without it
  return (
    <SheetCard label="Operation over time" reference={`last ${windowDays} days`} style={{ marginBottom: 20 }}>
      <p style={{ ...type.hint, margin: "0 0 4px" }}>
        One reading a day from the sync. The same series goes into the connector evidence document, so an assessor sees the control operating over time rather than on the day of the check.
      </p>
      {trends == null ? (
        <div style={{ ...type.meta, padding: "8px 0" }}>Loading…</div>
      ) : trends.length === 0 ? (
        <div style={{ ...emptyFrame, marginTop: 8, color: theme.color.muted, fontSize: 13 }}>No readings yet. The trend starts with the next sync.</div>
      ) : (
        trends.map((t) => <MetricRow key={t.key} t={t} />)
      )}
    </SheetCard>
  );
}
